import {
  computedAsync,
  useDebounceFn,
  type AsyncComputedOptions
} from '@vueuse/core'
import { defineStore } from 'pinia'
import { useCacheKey } from '@library/composables/use-cache-key'
import { useLocale } from '@library/composables/use-locale'
import { ApiLocale, SystemParameterKey } from '@library/enums'
import type { BaseOption, DataType, SystemParameter } from '@library/models'
import { useBfInjectStore } from '@library/stores/use-bf-inject-store'
import { isApiSuccess } from '@library/utils/functions/is-api-success'

/**
 * 系統參數選項類型
 * 擴展基礎選項，增加父級關聯資訊
 */
type SystemParameterOption<T extends SystemParameterKey> = BaseOption<
  T extends keyof DataType ? DataType[T] : string
> & {
  /** 父級代碼 */
  parentCode?: string
  /** 父級類型 */
  parentType?: string
}

/**
 * 系統參數資料介面
 * 包含多語系資料、當前語系資料、選項清單和標籤對應表
 */
export interface SystemParameterData<T extends SystemParameterKey> {
  /** 所有語系的原始資料 */
  allLangData: SystemParameter<T>[]
  /** 當前語系的資料 */
  currentLangData: SystemParameter<T> | null
  /** 下拉選單選項清單 */
  options: SystemParameterOption<T>[]
  /** 代碼對應標籤的對照表 */
  labels: Partial<
    Record<T extends keyof DataType ? DataType[T] : string, string>
  >
}

/**
 * 系統參數 Store
 * 集中管理所有系統參數，提供快取和多語系支援
 */
export const useSystemParameterStore = defineStore('system-parameter', () => {
  /**
   * 待處理的系統參數請求清單
   * 用於批次處理多個系統參數請求，提升效能
   */
  let pendingList: {
    key: SystemParameterKey
    resolve: (value: SystemParameter<SystemParameterKey>[]) => void
  }[] = []

  const { getBfApi } = useBfInjectStore()

  const { cacheKey } = useCacheKey()
  const { apiLang } = useLocale()

  /**
   * 非同步計算屬性的預設選項
   */
  const defaultOption: AsyncComputedOptions = { lazy: true }

  /**
   * 系統參數資料的初始值
   */
  const initialData = {
    allLangData: [],
    currentLangData: null,
    options: [],
    labels: {}
  } satisfies SystemParameterData<SystemParameterKey>

  /**
   * 所有語系的系統參數快取資料
   * 包含快取鍵值和系統參數陣列
   */
  const systemParameterAllLangData = ref<
    Partial<
      Record<
        SystemParameterKey,
        {
          cacheKey: number | null
          systemParameter: SystemParameter<SystemParameterKey>[]
        }
      >
    >
  >({})

  /**
   * 當前語系的系統參數資料
   * 根據當前語系從所有語系資料中篩選出對應的系統參數
   */
  const systemParameterCurrentLangData = computed(
    (): Partial<
      Record<SystemParameterKey, SystemParameter<SystemParameterKey>>
    > => {
      return Object.entries(systemParameterAllLangData.value).reduce(
        (acc, [key, value]) => {
          // 找到當前語系的資料，如果找不到則預設為 EN
          acc[key as SystemParameterKey] =
            value.systemParameter.find(({ lang }) => lang === apiLang.value) ??
            value.systemParameter.find(({ lang }) => lang === ApiLocale.EN)

          return acc
        },
        {} as Partial<
          Record<SystemParameterKey, SystemParameter<SystemParameterKey>>
        >
      )
    }
  )

  /** 後台系統登入帳號 */
  const backendLoginAccountData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.BACKEND_LOGIN_ACCOUNT)
      return getSystemParameterData(SystemParameterKey.BACKEND_LOGIN_ACCOUNT)
    },
    initialData,
    defaultOption
  )

  /** 商務卡到期月份 */
  const businessCardExpiryMonthData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.BUSINESS_CARD_EXPIRY_MONTH)
      return getSystemParameterData(
        SystemParameterKey.BUSINESS_CARD_EXPIRY_MONTH
      )
    },
    initialData,
    defaultOption
  )

  /** 商務卡收件月份 */
  const businessCardReceiveMonthData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.BUSINESS_CARD_RECEIVE_MONTH)
      return getSystemParameterData(
        SystemParameterKey.BUSINESS_CARD_RECEIVE_MONTH
      )
    },
    initialData,
    defaultOption
  )

  /** 商務卡申請狀態 */
  const businessCardStatusData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.BUSINESS_CARD_STATUS)
      return getSystemParameterData(SystemParameterKey.BUSINESS_CARD_STATUS)
    },
    initialData,
    defaultOption
  )

  /** 業務往來紀錄狀態 */
  const businessRecordStatusData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.BUSINESSRECORDSTATUS)
      return getSystemParameterData(SystemParameterKey.BUSINESSRECORDSTATUS)
    },
    initialData,
    defaultOption
  )

  /** 業務往來紀錄類型 */
  const businessRecordTypeData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.BUSINESSRECORDTYPE)
      return getSystemParameterData(SystemParameterKey.BUSINESSRECORDTYPE)
    },
    initialData,
    defaultOption
  )

  /** 業務往來紀錄類型分類 */
  const businessRecordTypeClassificationData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.BUSINESS_RECORD_TYPE_CLASSIFICATION)
      return getSystemParameterData(
        SystemParameterKey.BUSINESS_RECORD_TYPE_CLASSIFICATION
      )
    },
    initialData,
    defaultOption
  )

  /** 商機來源 */
  const businessSourceData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.BUSINESS_SOURCE)
      return getSystemParameterData(SystemParameterKey.BUSINESS_SOURCE)
    },
    initialData,
    defaultOption
  )

  /** 分類通知 */
  const categoryNotifyData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.CATEGORY_NOTIFY)
      return getSystemParameterData(SystemParameterKey.CATEGORY_NOTIFY)
    },
    initialData,
    defaultOption
  )

  /** 禮遇手動核銷狀態 */
  const courtesyWriteoffStatusData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.COURTESY_WRITEOFF_STATUS)
      return getSystemParameterData(SystemParameterKey.COURTESY_WRITEOFF_STATUS)
    },
    initialData,
    defaultOption
  )

  /** 城市 */
  const cityData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.CITY)
      return getSystemParameterData(SystemParameterKey.CITY)
    },
    initialData,
    defaultOption
  )

  /** 回饋項目狀態 */
  const commonCommodityStatusData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.COMMON_COMMODITY_STATUS)
      return getSystemParameterData(SystemParameterKey.COMMON_COMMODITY_STATUS)
    },
    initialData,
    defaultOption
  )

  /** 回饋項目審核項目 */
  const commonCommodityReviewItemData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.COMMON_COMMODITY_REVIEW_ITEM)
      return getSystemParameterData(
        SystemParameterKey.COMMON_COMMODITY_REVIEW_ITEM
      )
    },
    initialData,
    defaultOption
  )

  /** 企業員工上限哩程 */
  const companyEmployeeMaxMileageData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.COMPANY_EMPLOYEE_MAX_MILEAGE)
      return getSystemParameterData(
        SystemParameterKey.COMPANY_EMPLOYEE_MAX_MILEAGE
      )
    },
    initialData,
    defaultOption
  )

  /** 會員審核項目 */
  const companyEmployeeReviewItemData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.COMPANY_EMPLOYEE_REVIEW_ITEM)
      return getSystemParameterData(
        SystemParameterKey.COMPANY_EMPLOYEE_REVIEW_ITEM
      )
    },
    initialData,
    defaultOption
  )

  /** 合約自動續約年 */
  const contractAutoRenewYearData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.CONTRACT_AUTO_RENEW_YEAR)
      return getSystemParameterData(SystemParameterKey.CONTRACT_AUTO_RENEW_YEAR)
    },
    initialData,
    defaultOption
  )

  /** 合約延長失效月份 */
  const contractExtensionExpirationMonthData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.CONTRACT_EXTENSION_EXPIRATION_MONTH)
      return getSystemParameterData(
        SystemParameterKey.CONTRACT_EXTENSION_EXPIRATION_MONTH
      )
    },
    initialData,
    defaultOption
  )

  /** 合約審核項目 */
  const contractReviewItemData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.CONTRACT_REVIEW_ITEM)
      return getSystemParameterData(SystemParameterKey.CONTRACT_REVIEW_ITEM)
    },
    initialData,
    defaultOption
  )

  /** 合約狀態 */
  const contractStatusData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.CONTRACT_STATUS)
      return getSystemParameterData(SystemParameterKey.CONTRACT_STATUS)
    },
    initialData,
    defaultOption
  )

  /** 合約類型 */
  const contractTypeData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.CONTRACT_TYPE)
      return getSystemParameterData(SystemParameterKey.CONTRACT_TYPE)
    },
    initialData,
    defaultOption
  )

  /** 企業帳號異動動作 */
  const corporateAccountActionData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.CORPORATE_ACCOUNT_ACTION)
      return getSystemParameterData(SystemParameterKey.CORPORATE_ACCOUNT_ACTION)
    },
    initialData,
    defaultOption
  )

  /** 企業帳號狀態 */
  const corporateAccountStatusData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.CORPORATE_ACCOUNT_STATUS)
      return getSystemParameterData(SystemParameterKey.CORPORATE_ACCOUNT_STATUS)
    },
    initialData,
    defaultOption
  )

  /** 企業上限哩程 */
  const corporateMaxMileageData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.CORPORATE_MAX_MILEAGE)
      return getSystemParameterData(SystemParameterKey.CORPORATE_MAX_MILEAGE)
    },
    initialData,
    defaultOption
  )

  /** 客戶狀態 */
  const corporateStatusData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.CORPORATE_STATUS)
      return getSystemParameterData(SystemParameterKey.CORPORATE_STATUS)
    },
    initialData,
    defaultOption
  )

  /** 所在地 */
  const countryData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.COUNTRY)
      return getSystemParameterData(SystemParameterKey.COUNTRY)
    },
    initialData,
    defaultOption
  )

  /** 國家代碼 */
  const countryCodeData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.COUNTRY_CODE)
      return getSystemParameterData(SystemParameterKey.COUNTRY_CODE)
    },
    initialData,
    defaultOption
  )

  /** 商品來源 */
  const courtesySourceData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.COURTESY_SOURCE)
      return getSystemParameterData(SystemParameterKey.COURTESY_SOURCE)
    },
    initialData,
    defaultOption
  )

  /** 禮遇狀態 */
  const courtesyStatusData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.COURTESY_STATUS)
      return getSystemParameterData(SystemParameterKey.COURTESY_STATUS)
    },
    initialData,
    defaultOption
  )

  /** 幣別 */
  const currencyData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.CURRENCY)
      return getSystemParameterData(SystemParameterKey.CURRENCY)
    },
    initialData,
    defaultOption
  )

  /** 預設檔案路徑 */
  const defaultFilePathData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.DEFAULT_FILE_PATH)
      return getSystemParameterData(SystemParameterKey.DEFAULT_FILE_PATH)
    },
    initialData,
    defaultOption
  )

  /** 預設哩程券哩程數 */
  const defaultMileageData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.DEFAULT_MILEAGE)
      return getSystemParameterData(SystemParameterKey.DEFAULT_MILEAGE)
    },
    initialData,
    defaultOption
  )

  /** 預設點數序號 */
  const defaultPointGuidData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.DEFAULT_POINT_GUID)
      return getSystemParameterData(SystemParameterKey.DEFAULT_POINT_GUID)
    },
    initialData,
    defaultOption
  )

  /** 部門 */
  const departmentData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.DEPARTMENT)
      return getSystemParameterData(SystemParameterKey.DEPARTMENT)
    },
    initialData,
    defaultOption
  )

  /** 發放歷程狀態 */
  const distributionRecordStatusData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.DISTRIBUTION_RECORD_STATUS)
      return getSystemParameterData(
        SystemParameterKey.DISTRIBUTION_RECORD_STATUS
      )
    },
    initialData,
    defaultOption
  )

  /** 國內機場 */
  const domesticAirportData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.DOMESTIC_AIRPORT)
      return getSystemParameterData(SystemParameterKey.DOMESTIC_AIRPORT)
    },
    initialData,
    defaultOption
  )

  /** 成效來源 */
  const effectSourceData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.EFFECT_SOURCE)
      return getSystemParameterData(SystemParameterKey.EFFECT_SOURCE)
    },
    initialData,
    defaultOption
  )

  /** 會員尊享 - 類/分類選項  */
  const exclusiveOfferTypeData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.EXCLUSIVE_OFFER_TYPE)
      return getSystemParameterData(SystemParameterKey.EXCLUSIVE_OFFER_TYPE)
    },
    initialData,
    defaultOption
  )

  /** 會員尊享 - 狀態  */
  const exclusiveOfferStatusData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.EXCLUSIVE_OFFER_STATUS)
      return getSystemParameterData(SystemParameterKey.EXCLUSIVE_OFFER_STATUS)
    },
    initialData,
    defaultOption
  )
  /** 會員尊享 - 審核 - 執行動作  */
  const exclusiveOfferReviewItemData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.EXCLUSIVE_OFFER_REVIEW_ITEM)
      return getSystemParameterData(
        SystemParameterKey.EXCLUSIVE_OFFER_REVIEW_ITEM
      )
    },
    initialData,
    defaultOption
  )
  /** 會員尊享 - 權重 */
  const exclusiveOfferPriorityData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.EXCLUSIVE_OFFER_PRIORITY)
      return getSystemParameterData(SystemParameterKey.EXCLUSIVE_OFFER_PRIORITY)
    },
    initialData,
    defaultOption
  )

  /** 獎勵項目申請狀態 */
  const rewardsApplicationStatusData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.REWARDS_APPLICATION_STATUS)
      return getSystemParameterData(
        SystemParameterKey.REWARDS_APPLICATION_STATUS
      )
    },
    initialData,
    defaultOption
  )

  /** 點數/獎勵項目申請審核項目 */

  const rewardsApplicationReviewItemData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.REWARDS_APPLICATION_REVIEW_ITEM)
      return getSystemParameterData(
        SystemParameterKey.REWARDS_APPLICATION_REVIEW_ITEM
      )
    },
    initialData,
    defaultOption
  )

  /** 獎勵項目發放狀態 */
  const rewardsDistributionStatusData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.REWARDS_DISTRIBUTION_STATUS)
      return getSystemParameterData(
        SystemParameterKey.REWARDS_DISTRIBUTION_STATUS
      )
    },
    initialData,
    defaultOption
  )

  /** 點數/獎勵項目發放審核項目 */
  const rewardsDistributionReviewItemData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.REWARDS_DISTRIBUTION_REVIEW_ITEM)
      return getSystemParameterData(
        SystemParameterKey.REWARDS_DISTRIBUTION_REVIEW_ITEM
      )
    },
    initialData,
    defaultOption
  )

  /** 獎勵項目發放類型 */
  const rewardsDistributionTypeData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.REWARDS_DISTRIBUTION_TYPE)
      return getSystemParameterData(
        SystemParameterKey.REWARDS_DISTRIBUTION_TYPE
      )
    },
    initialData,
    defaultOption
  )

  /** 長榮組織部門 */
  const fMDomainData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.FM_DOMAIN)
      return getSystemParameterData(SystemParameterKey.FM_DOMAIN)
    },
    initialData,
    defaultOption
  )

  /** 串接組織人員帳號 */
  const fMServiceAccountData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.FM_SERVICE_ACCOUNT)
      return getSystemParameterData(SystemParameterKey.FM_SERVICE_ACCOUNT)
    },
    initialData,
    defaultOption
  )

  /** 串接組織人員密碼 */
  const fMServiceLatchkeyData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.FM_SERVICE_LATCHKEY)
      return getSystemParameterData(SystemParameterKey.FM_SERVICE_LATCHKEY)
    },
    initialData,
    defaultOption
  )

  /** Footer 連結 */
  const footerUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.FOOTER_URL)
      return getSystemParameterData(SystemParameterKey.FOOTER_URL)
    },
    initialData,
    defaultOption
  )

  /** FAQ 檔案 */
  const fAQFileData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.FAQ_FILE_LIST)
      return getSystemParameterData(SystemParameterKey.FAQ_FILE_LIST)
    },
    initialData,
    defaultOption
  )

  /** 指派禮遇影片 - 繁中 */
  const assignRewardsZhUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.ASSIGN_REWARDS_ZH_URL)
      return getSystemParameterData(SystemParameterKey.ASSIGN_REWARDS_ZH_URL)
    },
    initialData,
    defaultOption
  )

  /** 指派禮遇影片 - 英文 */
  const assignRewardsEnUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.ASSIGN_REWARDS_EN_URL)
      return getSystemParameterData(SystemParameterKey.ASSIGN_REWARDS_EN_URL)
    },
    initialData,
    defaultOption
  )

  /** 指派禮遇影片 - 韓文 */
  const assignRewardsKrUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.ASSIGN_REWARDS_KR_URL)
      return getSystemParameterData(SystemParameterKey.ASSIGN_REWARDS_KR_URL)
    },
    initialData,
    defaultOption
  )

  /** 指派禮遇影片 - 日文 */
  const assignRewardsJpUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.ASSIGN_REWARDS_JP_URL)
      return getSystemParameterData(SystemParameterKey.ASSIGN_REWARDS_JP_URL)
    },
    initialData,
    defaultOption
  )

  /** 指派禮遇影片 - 越南文 */
  const assignRewardsVnUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.ASSIGN_REWARDS_VN_URL)
      return getSystemParameterData(SystemParameterKey.ASSIGN_REWARDS_VN_URL)
    },
    initialData,
    defaultOption
  )

  /** 指派禮遇影片 - 泰文 */
  const assignRewardsThUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.ASSIGN_REWARDS_TH_URL)
      return getSystemParameterData(SystemParameterKey.ASSIGN_REWARDS_TH_URL)
    },
    initialData,
    defaultOption
  )

  /** 指派禮遇影片 - 柬埔寨文 */
  const assignRewardsKhUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.ASSIGN_REWARDS_KH_URL)
      return getSystemParameterData(SystemParameterKey.ASSIGN_REWARDS_KH_URL)
    },
    initialData,
    defaultOption
  )

  /** 綁定教學影片 - 繁中 */
  const bindingVideoZhUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.BINDING_VIDEO_ZH_URL)
      return getSystemParameterData(SystemParameterKey.BINDING_VIDEO_ZH_URL)
    },
    initialData,
    defaultOption
  )

  /** 綁定教學影片 - 英文 */
  const bindingVideoEnUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.BINDING_VIDEO_EN_URL)
      return getSystemParameterData(SystemParameterKey.BINDING_VIDEO_EN_URL)
    },
    initialData,
    defaultOption
  )

  /** 綁定教學影片 - 韓文 */
  const bindingVideoKrUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.BINDING_VIDEO_KR_URL)
      return getSystemParameterData(SystemParameterKey.BINDING_VIDEO_KR_URL)
    },
    initialData,
    defaultOption
  )

  /** 綁定教學影片 - 日文 */
  const bindingVideoJpUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.BINDING_VIDEO_JP_URL)
      return getSystemParameterData(SystemParameterKey.BINDING_VIDEO_JP_URL)
    },
    initialData,
    defaultOption
  )

  /** 綁定教學影片 - 越南文 */
  const bindingVideoVnUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.BINDING_VIDEO_VN_URL)
      return getSystemParameterData(SystemParameterKey.BINDING_VIDEO_VN_URL)
    },
    initialData,
    defaultOption
  )

  /** 綁定教學影片 - 奧地利德文 */
  const bindingVideoAtUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.BINDING_VIDEO_AT_URL)
      return getSystemParameterData(SystemParameterKey.BINDING_VIDEO_AT_URL)
    },
    initialData,
    defaultOption
  )

  /** 綁定教學影片 - 德文 */
  const bindingVideoDeUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.BINDING_VIDEO_DE_URL)
      return getSystemParameterData(SystemParameterKey.BINDING_VIDEO_DE_URL)
    },
    initialData,
    defaultOption
  )

  /** 綁定教學影片 - 義大利文 */
  const bindingVideoItUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.BINDING_VIDEO_IT_URL)
      return getSystemParameterData(SystemParameterKey.BINDING_VIDEO_IT_URL)
    },
    initialData,
    defaultOption
  )

  /** 差旅人禮遇綁定教學影片 - 繁中 */
  const travelerRewardVideoZhUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.TRAVELER_REWARD_VIDEO_ZH_URL)
      return getSystemParameterData(
        SystemParameterKey.TRAVELER_REWARD_VIDEO_ZH_URL
      )
    },
    initialData,
    defaultOption
  )

  /** 差旅人禮遇綁定教學影片 - 英文 */
  const travelerRewardVideoEnUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.TRAVELER_REWARD_VIDEO_EN_URL)
      return getSystemParameterData(
        SystemParameterKey.TRAVELER_REWARD_VIDEO_EN_URL
      )
    },
    initialData,
    defaultOption
  )

  /** 差旅人禮遇綁定教學影片 - 韓文 */
  const travelerRewardVideoKrUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.TRAVELER_REWARD_VIDEO_KR_URL)
      return getSystemParameterData(
        SystemParameterKey.TRAVELER_REWARD_VIDEO_KR_URL
      )
    },
    initialData,
    defaultOption
  )

  /** 差旅人禮遇綁定教學影片 - 日文 */
  const travelerRewardVideoJpUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.TRAVELER_REWARD_VIDEO_JP_URL)
      return getSystemParameterData(
        SystemParameterKey.TRAVELER_REWARD_VIDEO_JP_URL
      )
    },
    initialData,
    defaultOption
  )

  /** 差旅人禮遇綁定教學影片 - 越南文 */
  const travelerRewardVideoVnUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.TRAVELER_REWARD_VIDEO_VN_URL)
      return getSystemParameterData(
        SystemParameterKey.TRAVELER_REWARD_VIDEO_VN_URL
      )
    },
    initialData,
    defaultOption
  )

  /** 差旅人禮遇綁定教學影片 - 泰文 */
  const travelerRewardVideoThUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.TRAVELER_REWARD_VIDEO_TH_URL)
      return getSystemParameterData(
        SystemParameterKey.TRAVELER_REWARD_VIDEO_TH_URL
      )
    },
    initialData,
    defaultOption
  )

  /** 差旅人禮遇綁定教學影片 - 柬埔寨文 */
  const travelerRewardVideoKhUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.TRAVELER_REWARD_VIDEO_KH_URL)
      return getSystemParameterData(
        SystemParameterKey.TRAVELER_REWARD_VIDEO_KH_URL
      )
    },
    initialData,
    defaultOption
  )

  /** 酬賓機票艙等 - 手動核銷 */
  const fQTRRBDByWriteOffData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.FQTR_RBD_BY_WRITE_OFF)
      return getSystemParameterData(SystemParameterKey.FQTR_RBD_BY_WRITE_OFF)
    },
    initialData,
    defaultOption
  )

  /** 升等券訂位艙等 - 手動核銷 */
  const fQTURBDByWriteOffData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.FQTU_RBD_BY_WRITE_OFF)
      return getSystemParameterData(SystemParameterKey.FQTU_RBD_BY_WRITE_OFF)
    },
    initialData,
    defaultOption
  )

  /** 升等券訂位艙等 - 手動核銷 */
  const fQTUActualRBDByWriteOffData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.FQTU_ACTUAL_RBD_BY_WRITE_OFF)
      return getSystemParameterData(
        SystemParameterKey.FQTU_ACTUAL_RBD_BY_WRITE_OFF
      )
    },
    initialData,
    defaultOption
  )

  /** 專人交付地區 */
  const handDeliveryByAreaData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.HAND_DELIVERY_BY_AREA)
      return getSystemParameterData(SystemParameterKey.HAND_DELIVERY_BY_AREA)
    },
    initialData,
    defaultOption
  )

  /** 歷史禮遇狀態 */
  const historyCourtesyStatusData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.HISTORY_COURTESY_STATUS)
      return getSystemParameterData(SystemParameterKey.HISTORY_COURTESY_STATUS)
    },
    initialData,
    defaultOption
  )

  /** 產業別 */
  const industryData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.INDUSTRY)
      return getSystemParameterData(SystemParameterKey.INDUSTRY)
    },
    initialData,
    defaultOption
  )

  /** FFP註冊連結 */
  const invitationData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.INVITATION)
      return getSystemParameterData(SystemParameterKey.INVITATION)
    },
    initialData,
    defaultOption
  )

  /** 邀請信有效小時 */
  const invitationValidHoursData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.INVITATION_VALID_HOURS)
      return getSystemParameterData(SystemParameterKey.INVITATION_VALID_HOURS)
    },
    initialData,
    defaultOption
  )

  /** 識別碼動作 */
  const identifierActionData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.IDENTIFIER_ACTION)
      return getSystemParameterData(SystemParameterKey.IDENTIFIER_ACTION)
    },
    initialData,
    defaultOption
  )

  /** 雜項檔動作 */
  const miscellaneousActionData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.MISCELLANEOUS_ACTION)
      return getSystemParameterData(SystemParameterKey.MISCELLANEOUS_ACTION)
    },
    initialData,
    defaultOption
  )

  /** 官網購票異動動作 */
  const bookingSystemAction = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.BOOKING_SYSTEM_ACTION)
      return getSystemParameterData(SystemParameterKey.BOOKING_SYSTEM_ACTION)
    },
    initialData,
    defaultOption
  )

  /** 會員身分 */
  const identityData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.IDENTITY)
      return getSystemParameterData(SystemParameterKey.IDENTITY)
    },
    initialData,
    defaultOption
  )

  /** Join Us URL */
  const joinUsUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.JOIN_US_URL)
      return getSystemParameterData(SystemParameterKey.JOIN_US_URL)
    },
    initialData,
    defaultOption
  )

  /** 無限萬哩遊連結 */
  const myMileageUrlData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.MY_MILEAGE_URL)
      return getSystemParameterData(SystemParameterKey.MY_MILEAGE_URL)
    },
    initialData,
    defaultOption
  )

  /** 語系 */
  const langData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.LANG)
      return getSystemParameterData(SystemParameterKey.LANG)
    },
    initialData,
    defaultOption
  )

  /** 語系時間格式 */
  const langTimeFormatData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.LANG_TIME_FORMAT)
      return getSystemParameterData(SystemParameterKey.LANG_TIME_FORMAT)
    },
    initialData,
    defaultOption
  )

  /** 限量額度區間 */
  const limitedQuotaRangeData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.LIMITED_QUOTA_RANGE)
      return getSystemParameterData(SystemParameterKey.LIMITED_QUOTA_RANGE)
    },
    initialData,
    defaultOption
  )

  /** 月份 */
  const monthData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.MONTH)
      return getSystemParameterData(SystemParameterKey.MONTH)
    },
    initialData,
    defaultOption
  )

  /** 優惠標籤 */
  const offerTagData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.OFFER_TAG)
      return getSystemParameterData(SystemParameterKey.OFFER_TAG)
    },
    initialData,
    defaultOption
  )

  /** 鄉鎮市 */
  const optionalData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.OPTIONAL)
      return getSystemParameterData(SystemParameterKey.OPTIONAL)
    },
    initialData,
    defaultOption
  )

  /** 成效結算週期 - 達標制 */
  const performanceBillingCycleStandardData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.PERFORMANCE_BILLING_CYCLE_STANDARD)
      return getSystemParameterData(
        SystemParameterKey.PERFORMANCE_BILLING_CYCLE_STANDARD
      )
    },
    initialData,
    defaultOption
  )

  /** 點數到期月份 */
  const pointsExpiryMonthData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.POINTS_EXPIRY_MONTH)
      return getSystemParameterData(SystemParameterKey.POINTS_EXPIRY_MONTH)
    },
    initialData,
    defaultOption
  )

  /** 點數狀態 */
  const pointStatusData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.POINT_STATUS)
      return getSystemParameterData(SystemParameterKey.POINT_STATUS)
    },
    initialData,
    defaultOption
  )

  /** 方案審核項目 */
  const programReviewItemData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.PROGRAM_REVIEW_ITEM)
      return getSystemParameterData(SystemParameterKey.PROGRAM_REVIEW_ITEM)
    },
    initialData,
    defaultOption
  )

  /** 方案狀態 - 審核作業區 */
  const programStatusByReviewData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.PROGRAM_STATUS_BY_REVIEW)
      return getSystemParameterData(SystemParameterKey.PROGRAM_STATUS_BY_REVIEW)
    },
    initialData,
    defaultOption
  )

  /** 方案狀態 */
  const programStatusData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.PROGRAM_STATUS)
      return getSystemParameterData(SystemParameterKey.PROGRAM_STATUS)
    },
    initialData,
    defaultOption
  )

  /** 方案檻次 */
  const programThresholdData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.PROGRAM_THRESHOLD)
      return getSystemParameterData(SystemParameterKey.PROGRAM_THRESHOLD)
    },
    initialData,
    defaultOption
  )

  /** RBD */
  const rBDData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.RBD)
      return getSystemParameterData(SystemParameterKey.RBD)
    },
    initialData,
    defaultOption
  )

  /** 回饋項目手動核銷 RBD */
  const rBDByWriteOffData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.RBD_BY_WRITE_OFF)
      return getSystemParameterData(SystemParameterKey.RBD_BY_WRITE_OFF)
    },
    initialData,
    defaultOption
  )

  /** 退改方案 - Global */
  const rFDProgramByGlobalData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.RFD_PROGRAM_BY_GLOBAL)
      return getSystemParameterData(SystemParameterKey.RFD_PROGRAM_BY_GLOBAL)
    },
    initialData,
    defaultOption
  )

  /** 退改方案 - Local */
  const rFDProgramByLocalData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.RFD_PROGRAM_BY_LOCAL)
      return getSystemParameterData(SystemParameterKey.RFD_PROGRAM_BY_LOCAL)
    },
    initialData,
    defaultOption
  )

  /** 通路 */
  const routeData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.ROUTE)
      return getSystemParameterData(SystemParameterKey.ROUTE)
    },
    initialData,
    defaultOption
  )

  /** 稱謂 */
  const salutationData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.SALUTATION)
      return getSystemParameterData(SystemParameterKey.SALUTATION)
    },
    initialData,
    defaultOption
  )

  /** 寄件方式 */
  const shippingMethodData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.SHIPPING_METHOD)
      return getSystemParameterData(SystemParameterKey.SHIPPING_METHOD)
    },
    initialData,
    defaultOption
  )

  /** 簽核案件類型 */
  const signOffEntityTypeData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.SIGN_OFF_ENTITY_TYPE)
      return getSystemParameterData(SystemParameterKey.SIGN_OFF_ENTITY_TYPE)
    },
    initialData,
    defaultOption
  )

  /** 簽核狀態 */ const signOffStatusData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.SIGN_OFF_STATUS)
      return getSystemParameterData(SystemParameterKey.SIGN_OFF_STATUS)
    },
    initialData,
    defaultOption
  )

  /** 客戶終止合約對應合約狀態 */
  const terminatedContractStatusData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.TERMINATED_CONTRACT_STATUS)
      return getSystemParameterData(
        SystemParameterKey.TERMINATED_CONTRACT_STATUS
      )
    },
    initialData,
    defaultOption
  )

  /** 票券使用效期 */
  const ticketValidityPeriodData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.TICKET_VALIDITY_PERIOD)
      return getSystemParameterData(SystemParameterKey.TICKET_VALIDITY_PERIOD)
    },
    initialData,
    defaultOption
  )

  /** 差旅航線 */
  const travelAirlineData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.TRAVEL_AIRLINE)
      return getSystemParameterData(SystemParameterKey.TRAVEL_AIRLINE)
    },
    initialData,
    defaultOption
  )

  /** 差旅偏好 */
  const travelPreferenceData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.TRAVEL_PREFERENCE)
      return getSystemParameterData(SystemParameterKey.TRAVEL_PREFERENCE)
    },
    initialData,
    defaultOption
  )

  /** 會員尊享 - 時區 */
  const timeZoneData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.TIME_ZONE)
      return getSystemParameterData(SystemParameterKey.TIME_ZONE)
    },
    initialData,
    defaultOption
  )

  /** 上傳檔案目前空間 */
  const uploadFileCurrentSpaceData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.UPLOAD_FILE_CURRENT_SPACE)
      return getSystemParameterData(
        SystemParameterKey.UPLOAD_FILE_CURRENT_SPACE
      )
    },
    initialData,
    defaultOption
  )

  /** 上傳檔案最大空間 */
  const uploadFileMaximumSizeData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.UPLOAD_FILE_MAXIMUM_SIZE)
      return getSystemParameterData(SystemParameterKey.UPLOAD_FILE_MAXIMUM_SIZE)
    },
    initialData,
    defaultOption
  )

  /** 是否可以上傳檔案 */
  const whetherUploadFileData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.WHETHER_UPLOAD_FILE)
      return getSystemParameterData(SystemParameterKey.WHETHER_UPLOAD_FILE)
    },
    initialData,
    defaultOption
  )

  // 禮遇相關
  /** 回饋項目類型 - 第一層 - 分類選項 */
  const rewardCategoryData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.REWARD_CATEGORY, cacheKey.value)
      return getSystemParameterData(SystemParameterKey.REWARD_CATEGORY)
    },
    initialData,
    defaultOption
  )

  /** 回饋項目類型 - 第二層 - 機場禮遇 次分類選項 */
  const aPPRData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.APPR, cacheKey.value)
      return getSystemParameterData(SystemParameterKey.APPR)
    },
    initialData,
    defaultOption
  )

  /** 回饋項目類型 - 第二層 - 購票優惠碼 折扣方式 */
  const bTDCData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.BTDC)
      return getSystemParameterData(SystemParameterKey.BTDC)
    },
    initialData,
    defaultOption
  )

  /** 回饋項目類型 - 第二層 - 酬賓機票/升等券 航線選項 */
  const airlineData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.AIRLINE)
      return getSystemParameterData(SystemParameterKey.AIRLINE)
    },
    initialData,
    defaultOption
  )

  /** 回饋項目類型 - 第二層 - 商務卡 卡別選項 */
  const specData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.SPEC, cacheKey.value)
      return getSystemParameterData(SystemParameterKey.SPEC)
    },
    initialData,
    defaultOption
  )

  /** 回饋項目類型 - 第二層 - 套裝商品 地區選項 */
  const zoneData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.ZONE)
      return getSystemParameterData(SystemParameterKey.ZONE)
    },
    initialData,
    defaultOption
  )

  /** 回饋項目類型 - 第三層 - 購票優惠碼 折扣數(定價減價) */
  const fDISData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.FDIS)
      return getSystemParameterData(SystemParameterKey.FDIS)
    },
    initialData,
    defaultOption
  )

  /** 回饋項目類型 - 第三層 - 酬賓機票 艙等選項 */
  const fQTRRBDData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.FQTRRBD)
      return getSystemParameterData(SystemParameterKey.FQTRRBD)
    },
    initialData,
    defaultOption
  )

  /** 回饋項目類型 - 第三層 - 酬賓機票 艙等選項(台灣-香港/港澳) */
  const fQTRRBDBYHMData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.FQTRRBD_BY_HM)
      return getSystemParameterData(SystemParameterKey.FQTRRBD_BY_HM)
    },
    initialData,
    defaultOption
  )

  /** 回饋項目類型 - 第三層 - 升等券 艙等選項(全球不限航點/亞洲區間) */
  const fQTUData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.FQTU)
      return getSystemParameterData(SystemParameterKey.FQTU)
    },
    initialData,
    defaultOption
  )

  /** 回饋項目類型 - 第三層 - 升等券 艙等選項(台灣-香港/港澳) */
  const fQTUBYHMData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.FQTU_BY_HM)
      return getSystemParameterData(SystemParameterKey.FQTU_BY_HM)
    },
    initialData,
    defaultOption
  )

  /** 回饋項目類型 - 第三層 - 機場禮遇 地區選項(超額行李) */
  const pDBGData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.PDBG)
      return getSystemParameterData(SystemParameterKey.PDBG)
    },
    initialData,
    defaultOption
  )

  /** 回饋項目類型 - 第三層 - 購票優惠碼 折扣數(折扣減價 */
  const pDISData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.PDIS)
      return getSystemParameterData(SystemParameterKey.PDIS)
    },
    initialData,
    defaultOption
  )

  /** 企業官網購票艙等 */
  const cIBSCabinClassData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.CIBS_CABIN_CLASS)
      return getSystemParameterData(SystemParameterKey.CIBS_CABIN_CLASS)
    },
    initialData,
    defaultOption
  )

  /** 企業官網購票票價種類 */
  const cIBSDisplayTypeData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.CIBS_DISPLAY_TYPE)
      return getSystemParameterData(SystemParameterKey.CIBS_DISPLAY_TYPE)
    },
    initialData,
    defaultOption
  )

  /** 停機啟用 */
  const maintenanceEnabledData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.MAINTENANCE_ENABLED)
      return getSystemParameterData(SystemParameterKey.MAINTENANCE_ENABLED)
    },
    initialData,
    defaultOption
  )

  /** 停機起始日期 */
  const maintenanceStartDateData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.MAINTENANCE_START_DATE)
      return getSystemParameterData(SystemParameterKey.MAINTENANCE_START_DATE)
    },
    initialData,
    defaultOption
  )

  /** 停機截止日期 */
  const maintenanceEndDateData = computedAsync(
    () => {
      getSystemParameter(SystemParameterKey.MAINTENANCE_END_DATE)
      return getSystemParameterData(SystemParameterKey.MAINTENANCE_END_DATE)
    },
    initialData,
    defaultOption
  )

  /**
   * 根據鍵值取得系統參數
   * 如果快取鍵值相同則直接返回快取資料，否則發起新請求
   * @param key - 系統參數鍵值
   * @param cacheKey - 快取鍵值（用於判斷是否需要重新取得資料）
   * @returns 系統參數陣列
   */
  async function getSystemParameterByKey<T extends SystemParameterKey>(
    key: T,
    cacheKey: number | null = null
  ): Promise<SystemParameter<T>[]> {
    if (systemParameterAllLangData.value[key]?.cacheKey === cacheKey) {
      return (systemParameterAllLangData.value[key]?.systemParameter ??
        []) as SystemParameter<T>[]
    }

    const list = await new Promise<SystemParameter<SystemParameterKey>[]>(
      (resolve) => {
        pendingList.push({ key, resolve })
        getData()
      }
    )

    return list.filter((data) => data.type === key) as SystemParameter<T>[]
  }

  /**
   * 批次取得系統參數資料
   * 使用 debounce 機制合併多個請求，提升效能
   */
  const getData = useDebounceFn(async () => {
    const keys = [...new Set(pendingList.map(({ key }) => key))]

    const responseData = (await getBfApi().unAuth.getSysParameter(keys)).data

    if (isApiSuccess(responseData)) {
      pendingList.forEach(({ key, resolve }) => {
        if (keys.includes(key)) {
          resolve(responseData.data)
        }
      })

      pendingList = pendingList.filter(({ key }) => !keys.includes(key))
    }
  })

  /**
   * 取得格式化的系統參數資料
   * 將原始系統參數轉換為包含選項清單和標籤對照表的格式
   * @param key - 系統參數鍵值
   * @returns 格式化的系統參數資料
   */
  function getSystemParameterData<T extends SystemParameterKey>(
    key: T
  ): SystemParameterData<T> {
    const currentLangData = (systemParameterCurrentLangData.value[key] ??
      null) as SystemParameter<T> | null

    if (!currentLangData) {
      return initialData
    }

    const allLangData: SystemParameter<T>[] = (systemParameterAllLangData.value[
      key
    ]?.systemParameter ?? []) as SystemParameter<T>[]

    const options: SystemParameterOption<T>[] =
      currentLangData.paramList.map((data) => ({
        label: data.name,
        value: data.code,
        parentCode: data.parentCode,
        parentType: data.parentType,
        disabled: !data.enable
      })) ?? []

    const labels =
      currentLangData.paramList.reduce(
        (prev, { code, name }) => {
          prev[code] = name

          return prev
        },
        {} as Record<T extends keyof DataType ? DataType[T] : string, string>
      ) ?? {}

    return {
      allLangData,
      currentLangData,
      options,
      labels
    }
  }

  /**
   * 取得並快取系統參數
   * 根據快取鍵值判斷是否需要重新取得資料，並更新快取
   * @param key - 系統參數鍵值
   * @param cacheKey - 快取鍵值（用於判斷是否需要重新取得資料）
   * @returns 系統參數陣列
   */
  async function getSystemParameter<T extends SystemParameterKey>(
    key: T,
    cacheKey: number | null = null,
    useCache = true
  ): Promise<SystemParameter<T>[]> {
    if (
      systemParameterAllLangData.value[key]?.cacheKey === cacheKey &&
      useCache
    ) {
      return (systemParameterAllLangData.value[key]?.systemParameter ??
        []) as SystemParameter<T>[]
    }

    const systemParameter = await getSystemParameterByKey(key, cacheKey)

    systemParameterAllLangData.value[key] = {
      cacheKey,
      systemParameter
    }

    return systemParameter
  }

  return {
    /** 取得並快取系統參數 */
    getSystemParameter,
    /** 所有語系的系統參數資料 */
    systemParameterAllLangData,
    /** 後台系統登入帳號 */
    backendLoginAccountData,
    /** 商務卡到期月份 */
    businessCardExpiryMonthData,
    /** 商務卡收件月份 */
    businessCardReceiveMonthData,
    /** 商務卡申請狀態 */
    businessCardStatusData,
    /** 業務往來紀錄狀態 */
    businessRecordStatusData,
    /** 業務往來紀錄類型 */
    businessRecordTypeData,
    /** 業務往來紀錄類型分類 */
    businessRecordTypeClassificationData,
    /** 商機來源 */
    businessSourceData,
    /** 官網購票異動動作 */
    bookingSystemAction,
    /** 禮遇手動核銷狀態 */
    courtesyWriteoffStatusData,
    /** 分類通知 */
    categoryNotifyData,
    /** 回饋項目狀態 */
    commonCommodityStatusData,
    /** 回饋項目審核項目 */
    commonCommodityReviewItemData,
    /** 企業員工上限哩程 */
    companyEmployeeMaxMileageData,
    /** 會員審核項目 */
    companyEmployeeReviewItemData,
    /** 合約自動續約年 */
    contractAutoRenewYearData,
    /** 合約延長失效月份 */
    contractExtensionExpirationMonthData,
    /** 合約審核項目 */
    contractReviewItemData,
    /** 合約狀態 */
    contractStatusData,
    /** 合約類型 */
    contractTypeData,
    /** 企業帳號異動動作 */
    corporateAccountActionData,
    /** 企業帳號狀態 */
    corporateAccountStatusData,
    /** 企業上限哩程 */
    corporateMaxMileageData,
    /** 客戶狀態 */
    corporateStatusData,
    /** 城市 */
    cityData,
    /** 所在地 */
    countryData,
    /** 國家代碼 */
    countryCodeData,
    /** 商品來源 */
    courtesySourceData,
    /** 禮遇狀態 */
    courtesyStatusData,
    /** 幣別 */
    currencyData,
    /** 預設檔案路徑 */
    defaultFilePathData,
    /** 預設哩程券哩程數 */
    defaultMileageData,
    /** 預設點數序號 */
    defaultPointGuidData,
    /** 部門 */
    departmentData,
    /** 發放歷程狀態 */
    distributionRecordStatusData,
    /** 國內機場 */
    domesticAirportData,
    /** 成效來源 */
    effectSourceData,
    /** 會員尊享 - 權重 */
    exclusiveOfferPriorityData,
    /** 會員尊享 - 審核 - 執行動作 */
    exclusiveOfferReviewItemData,
    /** 會員尊享 - 狀態 */
    exclusiveOfferStatusData,
    /** 會員尊享 - 類/分類選項 */
    exclusiveOfferTypeData,
    /** 長榮組織部門 */
    fMDomainData,
    /** 串接組織人員帳號 */
    fMServiceAccountData,
    /** 串接組織人員密碼 */
    fMServiceLatchkeyData,
    /** Footer 連結 */
    footerUrlData,
    /** FAQ 檔案 */
    fAQFileData,
    /** 指派禮遇影片 - 繁中 */
    assignRewardsZhUrlData,
    /** 指派禮遇影片 - 英文 */
    assignRewardsEnUrlData,
    /** 指派禮遇影片 - 韓文 */
    assignRewardsKrUrlData,
    /** 指派禮遇影片 - 日文 */
    assignRewardsJpUrlData,
    /** 指派禮遇影片 - 越南文 */
    assignRewardsVnUrlData,
    /** 指派禮遇影片 - 泰文 */
    assignRewardsThUrlData,
    /** 指派禮遇影片 - 柬埔寨文 */
    assignRewardsKhUrlData,
    /** 綁定教學影片 - 繁中 */
    bindingVideoZhUrlData,
    /** 綁定教學影片 - 英文 */
    bindingVideoEnUrlData,
    /** 綁定教學影片 - 韓文 */
    bindingVideoKrUrlData,
    /** 綁定教學影片 - 日文 */
    bindingVideoJpUrlData,
    /** 綁定教學影片 - 越南文 */
    bindingVideoVnUrlData,
    /** 綁定教學影片 - 奧地利德文 */
    bindingVideoAtUrlData,
    /** 綁定教學影片 - 德文 */
    bindingVideoDeUrlData,
    /** 綁定教學影片 - 義大利文 */
    bindingVideoItUrlData,
    /** 差旅人禮遇綁定教學影片 - 繁中 */
    travelerRewardVideoZhUrlData,
    /** 差旅人禮遇綁定教學影片 - 英文 */
    travelerRewardVideoEnUrlData,
    /** 差旅人禮遇綁定教學影片 - 韓文 */
    travelerRewardVideoKrUrlData,
    /** 差旅人禮遇綁定教學影片 - 日文 */
    travelerRewardVideoJpUrlData,
    /** 差旅人禮遇綁定教學影片 - 越南文 */
    travelerRewardVideoVnUrlData,
    /** 差旅人禮遇綁定教學影片 - 泰文 */
    travelerRewardVideoThUrlData,
    /** 差旅人禮遇綁定教學影片 - 柬埔寨文 */
    travelerRewardVideoKhUrlData,
    /** 酬賓機票艙等 - 手動核銷 */
    fQTRRBDByWriteOffData,
    /** 升等券訂位艙等 - 手動核銷 */
    fQTURBDByWriteOffData,
    /** 升等券實際艙等 - 手動核銷 */
    fQTUActualRBDByWriteOffData,
    /** 專人交付地區 */
    handDeliveryByAreaData,
    /** 歷史禮遇狀態 */
    historyCourtesyStatusData,
    /** 產業別 */
    industryData,
    /** FFP 註冊連結 */
    invitationData,
    /** 邀請信有效小時 */
    invitationValidHoursData,
    /** 識別碼動作 */
    identifierActionData,
    /** 會員身分 */
    identityData,
    /** Join Us URL */
    joinUsUrlData,
    /** 無限萬哩遊連結 */
    myMileageUrlData,
    /** 語系 */
    langData,
    /** 語系時間格式 */
    langTimeFormatData,
    /** 限量額度區間 */
    limitedQuotaRangeData,
    /** 月份 */
    monthData,
    /** 雜項檔動作 */
    miscellaneousActionData,
    /** 優惠標籤 */
    offerTagData,
    /** 鄉鎮市 */
    optionalData,
    /** 成效結算週期 - 達標制 */
    performanceBillingCycleStandardData,
    /** 點數到期月份 */
    pointsExpiryMonthData,
    /** 點數狀態 */
    pointStatusData,
    /** 方案審核項目 */
    programReviewItemData,
    /** 方案狀態 - 審核作業區 */
    programStatusByReviewData,
    /** 方案狀態 */
    programStatusData,
    /** 方案檻次 */
    programThresholdData,
    /** RBD */
    rBDData,
    /** 回饋項目手動核銷 RBD */
    rBDByWriteOffData,
    /** 獎勵項目申請狀態 */
    rewardsApplicationStatusData,
    /** 獎勵項目發放狀態 */
    rewardsDistributionStatusData,
    /** 獎勵項目發放類型 */
    rewardsDistributionTypeData,
    /** 點數/獎勵項目申請審核項目 */
    rewardsApplicationReviewItemData,
    /** 點數/獎勵項目發放審核項目 */
    rewardsDistributionReviewItemData,
    /** 退改方案 - Global */
    rFDProgramByGlobalData,
    /** 退改方案 - Local */
    rFDProgramByLocalData,
    /** 通路 */
    routeData,
    /** 稱謂 */
    salutationData,
    /** 寄件方式 */
    shippingMethodData,
    /** 簽核案件類型 */
    signOffEntityTypeData,
    /** 簽核狀態 */
    signOffStatusData,
    /** 客戶終止合約對應合約狀態 */
    terminatedContractStatusData,
    /** 票券使用效期 */
    ticketValidityPeriodData,
    /** 差旅航線 */
    travelAirlineData,
    /** 差旅偏好 */
    travelPreferenceData,
    /** 會員尊享 - 時區 */
    timeZoneData,
    /** 上傳檔案目前空間 */
    uploadFileCurrentSpaceData,
    /** 上傳檔案最大空間 */
    uploadFileMaximumSizeData,
    /** 是否可以上傳檔案 */
    whetherUploadFileData,

    // 禮遇相關

    /** 回饋項目類型 - 第一層 - 分類選項 */
    rewardCategoryData,

    /** 回饋項目類型 - 第二層 - 酬賓機票/升等券 航線選項 */
    airlineData,
    /** 回饋項目類型 - 第二層 - 機場禮遇 次分類選項 */
    aPPRData,
    /** 回饋項目類型 - 第二層 - 購票優惠碼 折扣方式 */
    bTDCData,
    /** 回饋項目類型 - 第二層 - 商務卡 卡別選項 */
    specData,
    /** 回饋項目類型 - 第二層 - 套裝商品 地區選項 */
    zoneData,

    /** 回饋項目類型 - 第三層 - 購票優惠碼 折扣數(定價減價) */
    fDISData,
    /** 回饋項目類型 - 第三層 - 酬賓機票 艙等選項 */
    fQTRRBDData,
    /** 回饋項目類型 - 第三層 - 酬賓機票 艙等選項(台灣-香港/港澳) */
    fQTRRBDBYHMData,
    /** 回饋項目類型 - 第三層 - 升等券 艙等選項(全球不限航點/亞洲區間) */
    fQTUData,
    /** 回饋項目類型 - 第三層 - 升等券 艙等選項(台灣-香港/港澳) */
    fQTUBYHMData,
    /** 回饋項目類型 - 第三層 - 機場禮遇 地區選項(超額行李) */
    pDBGData,
    /** 回饋項目類型 - 第三層 - 購票優惠碼 折扣數(折扣減價) */
    pDISData,

    /** 企業官網購票艙等 */
    cIBSCabinClassData,
    /** 企業官網購票票價種類 */
    cIBSDisplayTypeData,

    /** 停機啟用 */
    maintenanceEnabledData,
    /** 停機起始日期 */
    maintenanceStartDateData,
    /** 停機截止日期 */
    maintenanceEndDateData
  }
})
